import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Avatar,
  Box,
  Button,
  Chip,
  CircularProgress,
  Paper,
  Stack,
  Step,
  StepLabel,
  Stepper,
  Typography,
} from "@mui/material";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";
import DownloadIcon from "@mui/icons-material/Download";
import BorderColorIcon from "@mui/icons-material/BorderColor";
import EditNoteIcon from "@mui/icons-material/EditNote";
import DoneAllIcon from "@mui/icons-material/DoneAll";
import ChecklistIcon from "@mui/icons-material/Checklist";
import GradingIcon from "@mui/icons-material/Grading";

import { getFlowOnSnapshot } from "../../services/backend/FlowService";

const TYPE_ICON = {
  create: BorderColorIcon,
  elaborate: EditNoteIcon,
  review: ChecklistIcon,
  approve: GradingIcon,
  sign: DoneAllIcon,
};

function statusOf(action) {
  // The action's status reflects the OWNER's progress; the most recent task
  // status reflects the latest activity (including post-rejection retries).
  const tasks = Array.isArray(action?.tasks) ? action.tasks : [];
  const last = tasks[tasks.length - 1];
  if (action?.status === "done") return "done";
  if (last?.status === "rejected") return "rejected";
  if (action?.status === "inProgress" || last?.status === "inProgress") return "inProgress";
  return "pending";
}

function StepBadge({ status }) {
  if (status === "done") return <Chip size="small" label="Finalizada" color="success" />;
  if (status === "rejected") return <Chip size="small" label="Devuelta" color="error" />;
  if (status === "inProgress") return <Chip size="small" label="En curso" color="warning" />;
  return <Chip size="small" label="Pendiente" variant="outlined" />;
}

export default function ResponseFlowProgress({ flowId }) {
  const navigate = useNavigate();
  const [flow, setFlow] = useState(null);

  useEffect(() => {
    if (!flowId) return undefined;
    const unsub = getFlowOnSnapshot(flowId, setFlow);
    return () => unsub && unsub();
  }, [flowId]);

  if (!flowId) return null;
  if (!flow) {
    return (
      <Paper sx={{ p: 3, mb: 3 }}>
        <Stack direction="row" alignItems="center" spacing={2}>
          <CircularProgress size={20} />
          <Typography variant="body2" color="text.secondary">
            Cargando estado del flujo...
          </Typography>
        </Stack>
      </Paper>
    );
  }

  const actions = Array.isArray(flow.actions) ? flow.actions : [];
  const activeIndex = (() => {
    const inProgress = actions.findIndex((a) => statusOf(a) === "inProgress" || statusOf(a) === "rejected");
    if (inProgress >= 0) return inProgress;
    const lastDone = actions.map(statusOf).lastIndexOf("done");
    return lastDone >= 0 ? lastDone : 0;
  })();

  const overallChip = (() => {
    if (flow.status === "done") return <Chip label="Aprobada" color="success" />;
    if (flow.status === "cancelled") return <Chip label="Cancelada" color="error" />;
    if (actions.some((a) => statusOf(a) === "rejected")) {
      return <Chip label="Devuelta — requiere atención" color="error" />;
    }
    return <Chip label="En aprobación" color="warning" variant="outlined" />;
  })();

  return (
    <Paper sx={{ p: 3, mb: 3 }}>
      <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 2 }}>
        <Box>
          <Typography variant="overline" color="text.secondary">
            Flujo de respuesta
          </Typography>
          <Typography variant="subtitle1">{flow.subject || "Respuesta supervisada"}</Typography>
        </Box>
        <Stack direction="row" spacing={1} alignItems="center">
          {overallChip}
          {flow.document?.file?.url && flow.status !== "done" && (
            <Button
              size="small"
              variant="outlined"
              color="primary"
              startIcon={<DownloadIcon />}
              href={flow.document.file.url}
              target="_blank"
              rel="noopener noreferrer"
            >
              Descargar borrador
            </Button>
          )}
          <Button
            size="small"
            variant="outlined"
            startIcon={<OpenInNewIcon />}
            onClick={() => navigate(`/flow/${flowId}`)}
          >
            Ver flujo
          </Button>
        </Stack>
      </Stack>

      <Stepper activeStep={activeIndex} alternativeLabel>
        {actions.map((action, idx) => {
          const s = statusOf(action);
          const Icon = TYPE_ICON[action.type] || BorderColorIcon;
          const owner = action.owners?.[0] || {};
          const ownerName = `${owner.name || ""} ${owner.lastName || ""}`.trim() || "Sin asignar";
          const when = action.signedAt
            ? (action.signedAt.format
                ? action.signedAt.format("LL, h:mm a")
                : new Date(action.signedAt).toLocaleString("es-CO"))
            : null;

          return (
            <Step
              key={idx}
              completed={s === "done"}
              active={idx === activeIndex && s !== "done"}
            >
              <StepLabel
                error={s === "rejected"}
                StepIconComponent={() => (
                  <Avatar
                    sx={{
                      bgcolor:
                        s === "done" ? "success.main"
                        : s === "rejected" ? "error.main"
                        : s === "inProgress" ? "warning.main"
                        : "grey.300",
                      width: 32,
                      height: 32,
                    }}
                  >
                    <Icon fontSize="small" />
                  </Avatar>
                )}
              >
                <Box
                  onClick={() => navigate(`/flow/${flowId}`)}
                  title="Abrir el flujo"
                  sx={{
                    cursor: "pointer",
                    borderRadius: 1,
                    px: 1,
                    py: 0.5,
                    transition: "background-color 0.15s",
                    "&:hover": { bgcolor: "action.hover" },
                  }}
                >
                  <Typography variant="caption" sx={{ fontWeight: 600 }}>
                    {action.actionName}
                  </Typography>
                  <Typography variant="caption" display="block" color="text.secondary">
                    {ownerName}
                  </Typography>
                  <Box sx={{ mt: 0.5 }}>
                    <StepBadge status={s} />
                  </Box>
                  {when && (
                    <Typography variant="caption" display="block" color="text.secondary" sx={{ mt: 0.5 }}>
                      {when}
                    </Typography>
                  )}
                </Box>
              </StepLabel>
            </Step>
          );
        })}
      </Stepper>
    </Paper>
  );
}
